import {useEffect, useState} from 'react';
import {toast} from 'react-toastify';

import {merchantService} from '@/services/merchants';
import {getErrorMessage} from '@/utils/errorMessage';
import {logRejection} from '@/utils/logRejection';

import type {MerchantOption} from './types';

// The picker searches the whole list client-side, and the endpoint caps a page
// at 100.
const MERCHANT_PAGE_LIMIT = 100;

/**
 * Merchant options for the invoice filter and the create form. Shared so the
 * two surfaces issue one request between them rather than each fetching the
 * same list.
 */
const useMerchantOptions = () => {
  const [merchants, setMerchants] = useState<MerchantOption[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const response = await merchantService.getBusinesses({
          page: 0,
          limit: MERCHANT_PAGE_LIMIT,
        });
        if (cancelled) {
          return;
        }
        setMerchants(
          (response.data ?? [])
            // `AfriexBusiness.id` is optional on the type. An option with no
            // value cannot be selected or submitted, so drop it rather than
            // rendering a dead row.
            .filter(({business}) => Boolean(business?.id))
            .map(({business}) => ({
              // `key` is the label the dropdown searches and renders; `value`
              // is what the form or filter submits.
              key: business.name || (business.id as string),
              value: business.id as string,
            })),
        );
      } catch (e) {
        if (!cancelled) {
          toast.error(getErrorMessage(e, 'Failed to load merchants'));
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };
    load().catch(logRejection('Failed to load merchants'));
    return () => {
      cancelled = true;
    };
  }, []);

  return {merchants, isLoading};
};

export default useMerchantOptions;
